import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EmptyPage from '../../empty-page/emptyPage';
import ReactLoading from 'react-loading';
import Pagination from '../../pagination/Pagination';
import OrgCampStrap from '../../orgCampStrap/OrgCampStrap';
import errorHandle from '../../../utils/errorHandle';
import { orgGetAccommodations } from '../../../api/orgAccommodation';

/*
 * "My Packages" list for an accommodation organizer account
 * (mounted under OrgCampLayout at /org/c/my-camps).
 *
 * This used to render a single hardcoded dummy row and never called
 * any API at all - real accommodation listings never showed up
 * here, only that one fake "Testing 1 fixed 111" card. It now
 * fetches the organizer's real accommodations from
 * GET org/accommodation/organizer.
 *
 * The property-type filter dropdown existed but did nothing: its
 * onChange wrote a `type` URL param that nothing ever read, and its
 * `value` was wired to `selectedStatus` instead of its own state
 * (so it visually tracked the Approved/Not Approved/Rejected
 * dropdown next to it). It's now backed by its own `type` param and
 * actually filters the list, using the real PROPERTY_TYPES values
 * from the backend schema instead of the previous
 * all/camp/villa/villa options (villa was listed twice, "camp"
 * isn't a real propertyType value).
 *
 * The backend list endpoint doesn't take status/type query params
 * (it just returns every accommodation owned by this organizer), so
 * filtering and pagination are done client-side here - the same
 * approach already used for a single organizer's own listings
 * elsewhere in the app, and fine at this scale.
 */

const PROPERTY_TYPE_OPTIONS = [
    'villa',
    'resort',
    'hotel',
    'homestay',
    'cottage',
    'hostel',
    'camping',
    'tent',
    'campsite',
    'cabin',
    'treehouse',
    'farmstay',
    'guesthouse',
    'house',
    'apartment',
    'other'
];

const OrgCampPackList = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const selectedStatus = searchParams.get('status') || 'active';
    const selectedType = searchParams.get('type') || 'all';
    const pageNumber = Number(searchParams.get('page')) || 1;
    const [accommodations, setAccommodations] = useState([]);
    const [nav, setNav] = useState(false);
    const [selectedPack, setSelectedPack] = useState('');
    const limit = 10;
    const offSet = (pageNumber - 1) * limit;

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);

    const updateURL = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);

            Object.keys(params).forEach((key) => {
                if (params[key] === '' || params[key] === null) {
                    delete current[key];
                } else {
                    current[key] = params[key];
                }
            });
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        let cancelled = false;

        async function loadAccommodations() {
            setLoading(true);
            try {
                const res = await orgGetAccommodations();
                if (!cancelled) {
                    setAccommodations(res?.data?.data || []);
                }
            } catch (error) {
                if (!cancelled) {
                    errorHandle(error);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        loadAccommodations();

        return () => {
            cancelled = true;
        };
    }, []);

    const filteredData = accommodations.filter((item) => {
        const statusMatch = item?.status === selectedStatus;
        const typeMatch = selectedType === 'all' || item?.propertyType === selectedType;
        return statusMatch && typeMatch;
    });

    const totalData = filteredData.length;
    const totalPages = Math.ceil(totalData / limit);
    const pagedData = filteredData.slice(offSet, offSet + limit);

    useEffect(() => {
        if (!totalPages) return;

        if (pageNumber > totalPages) {
            updateURL({ page: totalPages });
        }
        //eslint-disable-next-line
    }, [totalPages, pageNumber, updateURL]);

    const paginationClick = (number) => {
        if (totalPages > 1) {
            updateURL({ page: number });
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    if (nav && selectedPack) {
        window.open(`/org/c/details/${selectedPack}`, '_blank');
    }

    return (
        <div>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <div className='orgPack'>
                <div className='orgPackTripStrap'>
                    <div className='orgPackHeadSeaDiv alignCenter'>
                        <h1 className='orgBookingHeading'>My Packages</h1>
                        <div className='orgPackFilters'>
                            <select
                                name='status'
                                id='status'
                                className='select'
                                onChange={(e) => {
                                    updateURL({ status: e.target.value, page: 1 });
                                }}
                                value={selectedStatus}
                            >
                                <option value='active'>Approved</option>
                                <option value='inactive'>Not Approved</option>
                                <option value='rejected'>Rejected</option>
                            </select>
                            <select
                                name='type'
                                id='type'
                                className='select'
                                onChange={(e) => {
                                    updateURL({ type: e.target.value, page: 1 });
                                }}
                                value={selectedType}
                            >
                                <option value='all'>All Types</option>
                                {PROPERTY_TYPE_OPTIONS.map((type) => (
                                    <option key={type} value={type}>
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </option>
                                ))}
                            </select>
                            <button
                                className='orgPackCreateBtn blueBtn'
                                onClick={() => navigate('/org/c/create/camp')}
                            >
                                Create {window.innerWidth < 768 ? '' : 'Camp'}
                            </button>
                        </div>
                    </div>

                    {pagedData?.length === 0 && !loading ? (
                        <EmptyPage />
                    ) : (
                        <>
                            <OrgCampStrap
                                setNav={setNav}
                                data={pagedData}
                                setSelectedPack={setSelectedPack}
                                selectedStatus={selectedStatus}
                                onEdit={(id) => navigate(`/org/c/create/camp?new=false&type=edit&id=${id}`)}
                                onDuplicate={(id) => navigate(`/org/c/create/camp?new=false&type=duplicate&id=${id}`)}
                            />
                            <Pagination
                                paginationClick={paginationClick}
                                numberOfRows={totalData}
                                offSet={offSet}
                                pageNumber={pageNumber}
                                limit={limit}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrgCampPackList;
